<template>
  <div>
    <v-row no-gutters>
      <span class="textLight--text font-weight-medium text-h5 text-start">
        {{ $t("course.onGoingCourses.onGoingCourses") }}
      </span>
    </v-row>

    <v-row v-if="loading">
      <v-col>
        <ongoing-course-loader />
      </v-col>
    </v-row>

    <v-row v-else-if="courses.length">
      <v-col>
        <v-row>
          <v-col>
            <v-card flat class="rounded-lg primary--text">
              <v-row class="ps-5 ma-0" align="center">
                <v-col cols="6" sm="8">
                  <div class="font-weight-light text-start">
                    {{ $t("course.onGoingCourses.courses") }}
                  </div>
                </v-col>

                <v-col cols="3" sm="2">
                  <div class="d-flex flex-column flex-sm-row">
                    <div class="order-2 order-sm-1">
                      <v-icon size="20" v-if="arrow1" @click="startDateSorting">
                        mdi-arrow-up-thin
                      </v-icon>

                      <v-icon size="20" v-else @click="startDateSorting">
                        mdi-arrow-down-thin
                      </v-icon>
                    </div>

                    <div
                      class="order-1 order-sm-2 font-weight-light hand-cursor"
                      @click="startDateSorting"
                    >
                      {{ $t("course.onGoingCourses.startDate") }}
                    </div>
                  </div>
                </v-col>

                <v-col cols="3" sm="2">
                  <div class="d-flex flex-column flex-sm-row">
                    <div class="order-2 order-sm-1">
                      <v-icon size="20" v-if="arrow2" @click="endDateSorting">
                        mdi-arrow-up-thin
                      </v-icon>

                      <v-icon size="20" v-else> mdi-arrow-down-thin </v-icon>
                    </div>

                    <div
                      class="order-1 order-sm-2 font-weight-light hand-cursor"
                      @click="endDateSorting"
                    >
                      {{ $t("course.onGoingCourses.dueDate") }}
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col v-for="(course, index) in courses" :key="index" cols="12">
            <v-card flat class="rounded-lg">
              <v-row class="ps-4 ma-0" align="center">
                <v-col cols="6" md="8" class="px-0">
                  <div class="font-weight-light">
                    <div
                      class="
                        d-flex
                        flex-column
                        justify-start
                        flex-md-row
                        align-center
                      "
                    >
                      <v-img
                        class="rounded-lg mb-2 mb-md-0"
                        max-width="130"
                        max-height="100%"
                        height="80"
                        :src="
                          course.training
                            ? getImage(course.training.images)
                            : null
                        "
                        lazy-src="@/app/core-module/assets/images/logo_placeholder.png"
                        cover
                      />

                      <div
                        class="mx-0 mx-md-3 flex-grow-1"
                        style="text-align: start"
                      >
                        <p
                          class="
                            text--left
                            ma-0
                            mb-md-3
                            subtitle-1
                            textLight--text
                            text-ellipse-2
                          "
                          style="opacity: 70%; max-height: 80px"
                        >
                          {{ getLocalisedValue(course.training.title) }}
                        </p>

                        <div class="d-flex justify-space-between">
                          <h5 class="primary--text">
                            {{ $t("course.onGoingCourses.progress") }}
                          </h5>

                          <h5 class="primary--text">
                            {{ course.progress ? course.progress : 0 }}%
                          </h5>
                        </div>

                        <v-progress-linear :value="course.progress" />
                      </div>

                      <div class="pt-3 pt-md-0">
                        <v-btn
                          color="primaryLight"
                          class="primary--text"
                          depressed
                          @click="navigateToCourseDetailsPage(course._id)"
                        >
                          <v-icon left> mdi-play-outline </v-icon>
                          {{ $t("course.resume") }}
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </v-col>

                <v-col cols="3" md="2" class="px-0">
                  <div class="font-weight-light">
                    {{
                      course.startDate
                        ? moment(parseInt(course.startDate)).format(
                            "DD MMM YYYY"
                          )
                        : "-"
                    }}
                  </div>
                </v-col>

                <v-col cols="3" md="2" class="px-0">
                  <div
                    class="font-weight-light"
                    :class="dueDateClassStyle(course)"
                  >
                    <v-icon
                      v-if="dueDateClassStyle(course).length"
                      :class="dueDateClassStyle(course)"
                    >
                      mdi-alert-outline
                    </v-icon>

                    {{
                      course.endDate
                        ? moment(parseInt(course.endDate)).format("DD MMM YYYY")
                        : "-"
                    }}
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row v-else justify="center" align="center">
      <v-col cols="10" sm="8" lg="6">
        <empty-courses
          :heading="$t('course.noCoursesMessage')"
          :body="$t('course.contactCoordinator')"
          :isOnSide="false"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import EmptyCourses from "./EmptyCourses.vue";
import OngoingCourseLoader from "./skeleton-loaders/OngoingCourseLoader.vue";

export default {
  components: {
    EmptyCourses,
    OngoingCourseLoader,
  },
  props: {
    data: {
      type: Array,
    },
    loading: {
      type: Boolean,
    },
  },
  data() {
    return {
      courses: [],
      arrow1: false,
      arrow2: false,
    };
  },
  methods: {
    dueDateClassStyle(course) {
      return course.status !== "COMPLETED"
        ? course.days_difference < 0
          ? "error--text"
          : course.days_difference <= 5
          ? "warning--text"
          : ""
        : "";
    },
    startDateSorting() {
      if (this.data && this.data.length) {
        if (!this.arrow1) {
          this.data.sort((a, b) => a.startDate - b.startDate);
          this.arrow1 = true;
        } else if (this.arrow1) {
          this.data.sort((a, b) => b.startDate - a.startDate);
          this.arrow1 = false;
        }
      }
    },
    endDateSorting() {
      if (this.data && this.data.length) {
        if (!this.arrow2) {
          this.data.sort((a, b) => a.endDate - b.endDate);
          this.arrow2 = true;
        } else if (this.arrow2) {
          this.data.sort((a, b) => b.endDate - a.endDate);
          this.arrow2 = false;
        }
      }
    },
    navigateToCourseDetailsPage(trainingRegistrationId) {
      this.$router.push({
        name: "CourseDetails",
        params: { trainingRegistrationId },
      });
    },
  },
  watch: {
    data: {
      immediate: true,
      handler() {
        if (this.data.length) {
          this.courses = [];
          this.courses = this.data.map(obj => {
            obj.days_difference = this.$moment
              .duration(
                this.$moment(parseInt(obj.endDate)).diff(this.$moment())
              )
              .asDays();

            obj.progress = this.getTrainingProgressPercentage(obj);
            return obj;
          });
        }
      },
    },
  },
};
</script>
